import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import "./_avatar.scss";

const lightColors = [
  "#E5EFD9",
  "#D4D2DD",
  "#E1DCEF",
  "#D1D3E4",
  "#E8E4E4",
  "#E9DAD5",
  "#DFCEE7",
  "#DEDFD3",
  "#E7CECF",
  "#D5DFDA",
];

const Avatar = ({
  src,
  alt,
  width = "32px",
  height = "32px",
  borderRadius = "8px",
  orgName,
  backgroundColor,
  textColor,
  id,
}) => {
  const [imageError, setImageError] = useState(false);

  const generatedBackgroundColor = useMemo(() => {
    if (backgroundColor) return backgroundColor;

    if (id) {
      // Use id to generate consistent color for the same entity
      const colorIndex = id.charCodeAt(0) % lightColors.length;
      return lightColors[colorIndex];
    }

    // Fallback to random color if no id provided
    return lightColors[Math.floor(Math.random() * lightColors.length)];
  }, [backgroundColor, id]);

  const getDynamicFontSize = (width, height) => {
    // Extract numeric values from width and height (remove 'px', 'em', etc.)
    const widthNum = parseInt(width);
    const heightNum = parseInt(height);

    // Calculate the smaller dimension to base font size on
    const smallerDimension = Math.min(widthNum, heightNum);

    // Font size should be roughly 40-50% of the smaller dimension
    // with a minimum of 12px and maximum of 24px
    const calculatedFontSize = Math.max(
      12,
      Math.min(24, smallerDimension * 0.45)
    );

    return `${calculatedFontSize}px`;
  };

  return (
    <>
      {src && !imageError ? (
        <img
          style={{
            width: width,
            height: height,
            minWidth: width,
            minHeight: height,
            borderRadius: borderRadius,
            objectFit: "cover",
          }}
          src={src}
          alt={alt || "Organisation Image"}
          onError={() => setImageError(true)}
        />
      ) : (
        <p
          className="avatar-text"
          style={{
            width: width,
            height: height,
            minWidth: width,
            minHeight: height,
            borderRadius: borderRadius,
            backgroundColor: generatedBackgroundColor,
            // Only overridden when the caller knows its own background isn't
            // one of the light pastel fallbacks below (e.g. a solid brand
            // color or a translucent overlay on a dark surface) - the
            // default navy-blue text (_avatar.scss) stays correct for those.
            ...(textColor ? { color: textColor } : {}),
            fontSize: getDynamicFontSize(width, height),
          }}
        >
          {orgName?.charAt(0)?.toUpperCase() || "S"}
        </p>
      )}
    </>
  );
};

Avatar.propTypes = {
  src: PropTypes.string,
  alt: PropTypes.string,
  width: PropTypes.string,
  height: PropTypes.string,
  borderRadius: PropTypes.string,
  orgName: PropTypes.string,
  backgroundColor: PropTypes.string,
  textColor: PropTypes.string,
  id: PropTypes.string,
};

export default Avatar;
